/**
 * MonthLedger — pure projection of one YYYY-MM accounting row.
 * Canonical EOM / income-display formulas shared by Analytics.
 */

export interface MonthLedgerMonthInput {
  monthKey: string;
  /** Prior EOM for the first row; later rows use previous projected EOM. */
  baseCapitalBom?: number;
  grossTradingIncome: number;
  effectiveMargin: number;
  perTradeFees: number;
  cashAdded: number;
  withdrawn: number;
  ownCapitalCorrection: number;
  /** kind=estimated only — Income display + Tax Due est. paid. */
  estTaxPaid: number;
  /** estimated + balance-due + extension — Own Capital EOM. */
  taxCashOutflow: number;
  w2TaxPaid?: number;
  taxOwedIncremental?: number;
  taxRemainingMonth?: number | null;
}

export interface MonthLedgerRow {
  monthKey: string;
  grossTradingIncome: number;
  effectiveMargin: number;
  perTradeFees: number;
  estTaxPaid: number;
  taxCashOutflow: number;
  incomeAfterMargin: number;
  /** incomeAfterMargin − estTaxPaid (not tax due). */
  incomeDisplay: number;
  cashAdded: number;
  withdrawn: number;
  ownCapitalCorrection: number;
  baseCapitalBom: number;
  ownCapitalEom: number;
  rocPct: number | null;
  w2TaxPaid: number;
  taxOwedIncremental: number;
  taxRemainingMonth: number | null;
}

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Plan EOM if the month hits the net ROC target (same $ basis as Realized).
 * Margin is already inside targetIncomeNet — do not subtract it again.
 * Cash, stock lending, per-trade fees, and tax cash sit outside ROC.
 */
export function plannedOwnCapitalEom(input: {
  baseCapitalBom: number;
  targetIncomeNet: number;
  cashAdded?: number;
  withdrawn?: number;
  stockLendingIncome?: number;
  perTradeFees?: number;
  taxCashOutflow?: number;
}): number {
  return roundMoney(
    (Number(input.baseCapitalBom) || 0) +
      (Number(input.targetIncomeNet) || 0) +
      (Number(input.cashAdded) || 0) +
      (Number(input.stockLendingIncome) || 0) -
      (Number(input.withdrawn) || 0) -
      (Number(input.perTradeFees) || 0) -
      (Number(input.taxCashOutflow) || 0),
  );
}

/** Canonical single-month projection (EOM uses taxCashOutflow). */
export function buildMonthLedgerRow(input: MonthLedgerMonthInput): MonthLedgerRow {
  const baseCapitalBom = Number(input.baseCapitalBom) || 0;
  const grossTradingIncome = Number(input.grossTradingIncome) || 0;
  const effectiveMargin = Number(input.effectiveMargin) || 0;
  const perTradeFees = Number(input.perTradeFees) || 0;
  const cashAdded = Number(input.cashAdded) || 0;
  const withdrawn = Number(input.withdrawn) || 0;
  const ownCapitalCorrection = 0;
  const estTaxPaid = Number(input.estTaxPaid) || 0;
  const taxCashOutflow = Number(input.taxCashOutflow) || 0;
  const incomeAfterMargin = roundMoney(grossTradingIncome - effectiveMargin);
  const incomeDisplay = roundMoney(incomeAfterMargin - estTaxPaid);
  const ownCapitalEom = roundMoney(
    baseCapitalBom +
      grossTradingIncome +
      cashAdded -
      withdrawn -
      perTradeFees -
      taxCashOutflow -
      effectiveMargin,
  );
  const rocPct =
    baseCapitalBom > 0 ? (incomeAfterMargin / baseCapitalBom) * 100 : null;

  return {
    monthKey: input.monthKey,
    grossTradingIncome,
    effectiveMargin,
    perTradeFees,
    estTaxPaid,
    taxCashOutflow,
    incomeAfterMargin,
    incomeDisplay,
    cashAdded,
    withdrawn,
    ownCapitalCorrection,
    baseCapitalBom,
    ownCapitalEom,
    rocPct,
    w2TaxPaid: Number(input.w2TaxPaid) || 0,
    taxOwedIncremental: Number(input.taxOwedIncremental) || 0,
    taxRemainingMonth:
      input.taxRemainingMonth != null && Number.isFinite(input.taxRemainingMonth)
        ? Number(input.taxRemainingMonth)
        : null,
  };
}

/**
 * Running EOM timeline. Pass months sorted ascending; uses each row's
 * effectiveMargin as given (caller owns predicted-margin solve).
 */
export function buildMonthLedgerTimeline(
  months: MonthLedgerMonthInput[],
  openingBom = 0,
): Map<string, MonthLedgerRow> {
  const out = new Map<string, MonthLedgerRow>();
  let running = openingBom;
  for (const month of months) {
    const row = buildMonthLedgerRow({ ...month, baseCapitalBom: running });
    out.set(row.monthKey, row);
    running = row.ownCapitalEom;
  }
  return out;
}
